import * as React from "react";
import CustomBackdrop from "../../components/CustomBackdrop";
import { useLocation, useNavigate } from "react-router-dom";
import ApiController from "../../controllers/api";

//* 'ADMIN', ADMINISTRADOR
//* 'HOLDER', TITULAR
//* 'SUPPORT', SOPORTE
//* 'USER', USUARIO GENERICO

const AuthContext = React.createContext();
export const useAuth = () => React.useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const apiInstance = React.useMemo(
    () => new ApiController(process.env.REACT_APP_API_URL),
    [],
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [initializing, setInitializing] = React.useState(true); //ESTADO DE PRIMERA CARGA

  //* DEFINICIÓN DE RUTAS
  const PUBLIC_ROUTES = ["/"];
  const ROUTE_PERMISSIONS = {
    "/unauthorized": ["ADMIN", "HOLDER", "SUPPORT", "USER"],
    "/info": ["ADMIN", "HOLDER", "SUPPORT", "USER"],
    "/inventory": ["ADMIN", "HOLDER", "SUPPORT"],
    "/part": ["ADMIN", "HOLDER", "SUPPORT"],
    "/property": ["ADMIN", "HOLDER", "SUPPORT"],
    "/building": ["ADMIN", "SUPPORT"],
    "/area": ["ADMIN", "SUPPORT"],
    "/asign-safe": ["ADMIN", "SUPPORT"]
  };

  //* BUSCAR RUTA MÁS ESPECIFICA
  const matchRoute = React.useCallback((path) => {
    const routes = Object.keys(ROUTE_PERMISSIONS).sort((a, b) => b.length - a.length);
      return routes.find((r) => path.startsWith(r)) || null;
    }, []
  );

  //* VERIFICACIÓN DE ROL
  const hasPermission = React.useCallback(
    (requiredRoles = []) => {
      if (!user) return false;
      return requiredRoles.includes(user.role);
    }, [user]
  );

  //* VERIFICIÓN DE RUTA
  const hasAccess = React.useCallback((path) => {
      if (!user) return false;
      const matched = matchRoute(path);
      if (!matched) return true;
      return ROUTE_PERMISSIONS[matched].includes(user.role);
    },[user, matchRoute]
  );

  //* INICIO DE SESIÓN
  const login = React.useCallback(
    async (username, password) => {
      setLoading(true);
      try {
        const res = await apiInstance.postLogin("/auth/login", {
          username,
          password,
        });
        if (res.success && res.data) {
          const { USERNAME, ROLE } = JSON.parse(res.data);
          setUser({ username: USERNAME, role: ROLE });
          return { success: true };
        }
        return { success: false };
      } catch (error) {
        return { success: false };
      } finally {
        setLoading(false);
      }
    }, [apiInstance]
  );

  //* CERRAR SESIÓN
  const logout = React.useCallback(async () => {
    setLoading(true);
    try {
      await apiInstance.get("/auth/logout");
      setUser(null);
      sessionStorage.removeItem("auth_from");
      navigate("/", { replace: true });
      return { success: true };
    } catch (error) {
      setUser(null);
      sessionStorage.removeItem("auth_from");
      navigate("/", { replace: true });
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [apiInstance]);


  //* REVISAR LA SESION ACTIVA EN EL SERVIDOR
  // initial === true SOLO PARA LA PRIMERA VERIFICACIÓN (EVITAR FLASH)
  const checkSession = React.useCallback(async (initial = false) => {
    if (initial) setInitializing(true);
    try {
      const res = await apiInstance.get("/auth/checkme");
      if (res && res.success && res.data) {
        const { USERNAME, ROLE } = JSON.parse(res.data);
        setUser({ username: USERNAME, role: ROLE });
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      if (initial) setInitializing(false);//TERMINAR LA INICIALIZACIÓN SOLO EN LA PRIMERA PASADA
    }
  }, [apiInstance]);

  //* REFRESH MANUAL
  const refresh = React.useCallback(() => checkSession(), [checkSession]);

  //* PROTECCIÓN DE RUTAS
  React.useEffect(() => {
    // ESPERAR A QUE TERMINE LA INICIALIZACIÓN Y LA CARGA
    if (initializing || loading) return;

    // SI EL USUARIO ESTA AUTENTICADO Y ESTA EN EL '/' REDIRIGIR INMEDIATAMENTE
    if (user && pathname === "/") {
      // PREFIRIR location.state.from, LUEGO sessionStorage (SI SE GUARDO FORZAR EL LOGIN), LUEGO '/inventory' QUE ES LA RUTA POR DEFECTO
      const fromPath = location.state?.from?.pathname || sessionStorage.getItem("auth_from") || "/inventory";
      sessionStorage.removeItem("auth_from");

      if (fromPath && fromPath !== pathname) navigate(fromPath, { replace: true });
      return;
    }

    //RUTAS PUBLICAS SIEMPRE ACCESIBLES
    if (PUBLIC_ROUTES.includes(pathname)) return;

    //SI NO EXISTE EL USUARIO, FORZAR EL LOGIN Y GUARDAR LA RUTA ORIGINAL PARA VOLVER DEPUÉS
    if (!user && pathname !== "/") {
      //GUARDAR SOLO SI NO SE ENCUENTRA EN "/"
      sessionStorage.setItem("auth_from", pathname + window.location.search);
      navigate("/", { replace: true, state: { from: location } });
      return;
    }

    //SI EL USUARIO EXISTE VERIFICAR LOS PERMISOS
    if (user) {
      const matched = matchRoute(pathname);
      if (matched && !ROUTE_PERMISSIONS[matched].includes(user.role)) {
        if (pathname !== "/unauthorized") navigate("/unauthorized", { replace: true });
      }
    }
  }, [user, initializing, loading, pathname, navigate, matchRoute, location]);

  //* VERIFICACIÓN INICIAL
  React.useEffect(() => {
    // PRIMERA VERIFICAIÓN CON initial PARA EVITAR FLASHES
    checkSession(true);
    const n = 1; // VERIFICACION CADA MINUTO
    const interval = setInterval(() => checkSession(false), n * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkSession]);

  //* RENDER CONDICIONAL
  if (initializing) return <CustomBackdrop open={initializing} />;
  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        refresh,
        loading,
        initializing,
        hasPermission,
        hasAccess,
      }}
    >
      <main>{children}</main>
    </AuthContext.Provider>
  );
};